/* eslint-disable no-mixed-spaces-and-tabs */
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Editable from '../../Utils/editable';
import './dashCard.css';
import DashboardAddModal from './dashboardAddModal';
import { TopicCard } from './singleTopic';
import TopicAddModal from './topicAddModal';
import { DashCard } from './singleDash';
import moment from 'moment';
import { stateNotificationsEnum } from '../../Utils/notification';
import {
	setSelectedSection,
	setTab
} from '../../../redux/actions/sideBarAction';
import { useDispatch } from 'react-redux';
import Multiselect from './rolesMultiselect';
import { BsCheckLg, BsClipboard, BsXLg } from 'react-icons/bs';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import { SetNotificationAction } from '../../../redux/actions/notificationAction';
import { globalEnv } from '../../../config/baseConfig';
import { DataNotFoundAnimation } from '../../Utils/ErrorAnimations/dataNotFoundAnim';
import LoadingAnimComponent from '../../Utils/LoadingAnimations/loadingAnimation';
import LazyImage from '../../Tables/TablesList/Components/imageComponent';
import {
	hideSearchbarAction,
	setSearchPlaceholderAction
} from '../../../redux/actions/searchBarAction';
import { axiosInstance } from '../../../config/axiosInstance';
import { Switch } from '@headlessui/react';

enum SubscriptionStatus {
	INCOMPLETE = 'incomplete',
	INCOMPLETE_EXPIRED = 'incomplete_expired',
	TRIALING = 'trialing',
	ACTIVE = 'active',
	PAST_DUE = 'past_due',
	CANCELED = 'canceled',
	UNPAID = 'unpaid',
	PAUSED = 'paused'
}

export default function UserAccount() {
	const [isChanged, setIsChanged] = useState(false);

	const [userId, setUserId] = useState('');
	const [username, setUsername] = useState('');
	const [email, setEmail] = useState('');
	const [photo, setPhoto] = useState(null);
	const [location, setLocation] = useState('');
	const [isSocial, setIsSocial] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);
	const [lastActive, setLastActive] = useState();
	const [subscribedToEmails, setSubscribedToEmails] = useState(false);
	const [isBlocked, setIsBlocked] = useState(false);
	const [loginAttempts, setLoginAttempts] = useState(0);
	const [blockExpires, setblockExpires] = useState('');
	const [signupDate, setSignupDate] = useState(null);
	const [defaultDash, setDefaultDash] = useState('');
	const [singleDash, setSingleDash] = useState(false);
	const [premiumTester, setPremiumTester] = useState(false);
	const [subscriptions, setSubscriptions] = useState([]);
	const usernameRef = useRef<null>(null);
	const locationRef = useRef<null>(null);
	const emailRef = useRef<null>(null);

	const [isAddTopicModalOpen, setTopicModalOpen] = useState(false);
	const [isAddDashModalOpen, setDashModalOpen] = useState(false);

	const [dashboards, setDashboards] = useState<any[]>([]);
	const [topics, setTopics] = useState<any[]>([]);
	const [devices, setDevices] = useState<any[]>([]);
	const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [activeExtensions, setActiveExtensions] = useState<any[]>([]);
	const [isCancellationLinkCopied, setIsCancellationLinkCopied] =
		useState(false);

	const { user }: any = useTypedSelector((state) => state.auth);
	const { id } = useParams();
	const [isProfile] = useState(user._id === id);
	const dispatch = useDispatch();
	dispatch(setSelectedSection('User Details'));
	dispatch(hideSearchbarAction());

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	useEffect(() => {
		if (isProfile) dispatch(setTab('profile'));
		else dispatch(setTab('documents'));
	}, [isProfile]);

	const cancellationLink = encodeURI(
		`https://billing.stripe.com/p/login/cNi8wQ1Hbaild4N8aN7kc00?prefilled_email=${email}`
	);

	const copyCancellationLink = async () => {
		try {
			await navigator.clipboard.writeText(cancellationLink);
			setIsCancellationLinkCopied(true);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.SUCCESS,
					message: 'Cancellation link copied to clipboard.'
				})
			);
		} catch {
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't copy the cancellation link."
				})
			);
		}
	};

	useEffect(() => {
		if (!isCancellationLinkCopied) return;
		const timeout = setTimeout(() => setIsCancellationLinkCopied(false), 2000);
		return () => clearTimeout(timeout);
	}, [isCancellationLinkCopied]);

	// handleResize() & dimensions are added to update the dashboard card text size when resizing the window
	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	async function fetchUserData(id: string, token: string) {
		const config = {
			headers: {
				Authorization: 'Bearer ' + token
			}
		};
		const { data } = await axiosInstance.get(
			`${globalEnv.BASE_URL}/admin/user/${id}/get-detailed`,
			config
		);
		return data;
	}

	const { data, isLoading, isError } = useQuery(
		['users', id],
		() => fetchUserData(id!, ACCESS_TOKEN),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 5000
		}
	);

	const navigate = useNavigate();
	const routerLocation = useLocation();

	// Context passed by the list page: ordered ids of the page the user came
	// from, enabling previous/next cycling and a back that preserves the query.
	const listNav = (routerLocation.state ?? {}) as {
		fromList?: boolean;
		userIds?: string[];
		userIndex?: number;
	};
	const hasListContext =
		Array.isArray(listNav.userIds) && typeof listNav.userIndex === 'number';

	const goBackToList = () => {
		// Going back restores the list URL (sort/page/search params) instead of
		// pushing a fresh default list. Fallback covers direct deep links.
		if (routerLocation.key !== 'default') navigate(-1);
		else navigate('../documents');
	};

	const goToSibling = (delta: number) => {
		if (!hasListContext) return;
		const nextIndex = listNav.userIndex! + delta;
		if (nextIndex < 0 || nextIndex >= listNav.userIds!.length) return;
		// replace: keeps history at one entry deep, so back always returns to
		// the list no matter how many users were cycled through.
		navigate(`../user/${listNav.userIds![nextIndex]}`, {
			replace: true,
			state: { ...listNav, userIndex: nextIndex }
		});
	};

	const setDefaultDashState = (dashboards: any[]) => {
		if (dashboards.length === 1) setSingleDash(true);
		dashboards.some((element) => {
			if (element.default) setDefaultDash(element._id);
		});
	};

	useEffect(() => {
		if (data != null) {
			setUserId(data._id);
			setUsername(data.username);
			setLocation('');
			setEmail(data.email);
			setSignupDate(data.createdAt);
			setLoginAttempts(data.loginAttempts);
			setIsDisabled(data.isDisabled);
			setLastActive(data.lastActive);
			setSubscribedToEmails(data.subscribedToEmails);
			setIsSocial(data.isSocial);
			setSelectedRoles(data.roles);
			setPremiumTester(data.premiumTester || false);
			setPhoto(data.photo ?? null);
			// Sort by per-extension last active (most recent first) so the
			// extension/topic the user is currently on surfaces at the front.
			const sortedActiveExtensions = [...(data.activeExtensions || [])].sort(
				(a, b) => {
					const aTime = a.userActivity?.[0]?.lastActive
						? new Date(a.userActivity[0].lastActive).getTime()
						: 0;
					const bTime = b.userActivity?.[0]?.lastActive
						? new Date(b.userActivity[0].lastActive).getTime()
						: 0;
					return bTime - aTime;
				}
			);
			setActiveExtensions(sortedActiveExtensions);

			setDashboards(data.dashboards);
			setDefaultDashState(data.dashboards);

			setTopics(data.topics);

			setDevices(data.refreshTokens);

			setblockExpires(data.blockExpires);

			// Explicit true/false so state is correct when cycling between users
			// without unmounting (prev/next arrows).
			setIsBlocked(moment(data.blockExpires) > moment());
			setIsChanged(false);

			setSubscriptions(data.subscription || []);
		}
	}, [data]);

	useEffect(() => {
		if (dashboards.length > 1) setSingleDash(false);
		else setSingleDash(true);
	}, [dashboards]);

	type deviceProps = {
		_id: string;
		deviceId: string;
		refreshToken: string;
		expirationDate: string;
		number: number;
	};

	const DeviceCard = ({
		_id,
		deviceId,
		refreshToken,
		expirationDate,
		number
	}: deviceProps) => {
		return (
			<div className="relative carousel-item">
				<div
					className="group block p-6 max-w-sm min-w-[300px] bg-oldgray-300 rounded-xl border shadow-xl mx-3
					bg-card-light border-border-light dark:bg-card-dark dark:border-border-dark  transition-colors duration-500"
				>
					<h3 className="mb-6 text-2xl font-bold tracking-tight text-oldgray-800 dark:text-white">
						{'Device #' + number}
					</h3>

					<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
						{'Device ID: ' + deviceId}
					</h5>

					<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
						{'Refresh Token ID: ' + refreshToken}
					</h5>

					<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
						{'Token Expiration date: ' +
							moment(expirationDate).format('MM/DD/YYYY - HH:mm')}
					</h5>
					<div className="opacity-0 group-hover:opacity-100 transition-all duration-500">
						<button
							className="absolute top-2 left-5 btn-error btn-xs btn btn-circle btn-outline shadow-md"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								// removeImage();
							}}
						>
							<BsXLg />
						</button>
					</div>
				</div>
			</div>
		);
	};

	const SignupAndActivityDates = () => {
		return (
			<div>
				{/* <div className="text-gray-700">
					<i className="fas fa-university mr-2 text-sm text-gray-500"></i>
					<span
						className="text-sm font-bold text-secondary-text-light dark:text-secondary-text-dark
								   transition-colors duration-500"
					>
						{'Last activity: '}
					</span>
					<span className="text-sm font-bold dark:text-gray-400 transition-colors duration-500 text-gray-600">
						15/03/2022 - 20:00
					</span>
				</div> */}
				<div className="mb-2 text-gray-700">
					<i className="fas fa-university mr-2 text-sm text-gray-500 "></i>
					<span
						className="text-sm font-bold text-secondary-text-light dark:text-secondary-text-dark
								   transition-colors duration-500"
					>
						{'Signup date: '}
					</span>
					<span className="text-sm font-bold dark:text-gray-400 transition-colors duration-500 text-gray-600">
						{moment(signupDate).format('MM/DD/YYYY - HH:mm')}
					</span>
				</div>

				<div className="mb-2 text-gray-700">
					<i className="fas fa-university mr-2 text-sm text-gray-500 "></i>
					<span
						className="text-sm font-bold text-green-600
								   transition-colors duration-500"
					>
						{'Last active: '}
					</span>
					<span className="text-sm font-bold dark:text-gray-400 transition-colors duration-500 text-gray-600">
						{moment(lastActive).format('MM/DD/YYYY - HH:mm')}
					</span>
				</div>
			</div>
		);
	};

	const GeneralData = () => {
		return (
			<div className="flex flex-wrap lg:justify-end lg:py-4 lg:pt-4 pt-8 gap-2">
				<div className="relative group px-4 pb-2 dark:bg-gray-700 bg-gray-200 rounded-xl">
					<span className="w-full whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-400">
						{'Receive emails'}
					</span>
					<div className="mt-0.5 w-full px-6">
						<Switch
							checked={subscribedToEmails}
							onChange={() => {
								updateUserEmailSub.mutate();
								setSubscribedToEmails(!subscribedToEmails);
							}}
							className={`${
								subscribedToEmails ? 'bg-purple-600' : 'bg-gray-600'
							} relative inline-flex h-6 w-11 items-center rounded-full`}
						>
							<span
								className={`${
									subscribedToEmails ? 'translate-x-6' : 'translate-x-1'
								} inline-block h-4 w-4 transform rounded-full bg-gray-200 transition-all ease-in-out duration-200`}
							/>
						</Switch>
					</div>
				</div>
				<div className="relative group px-4 pb-2 dark:bg-gray-700 bg-gray-200 rounded-xl">
					<span className="w-full whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-400">
						{'Premium Tester'}
					</span>
					<div className="mt-0.5 w-full px-6">
						<Switch
							checked={premiumTester}
							onChange={() => {
								setPremiumTester(!premiumTester);
								setIsChanged(true);
							}}
							className={`${
								premiumTester ? 'bg-purple-600' : 'bg-gray-600'
							} relative inline-flex h-6 w-11 items-center rounded-full`}
						>
							<span
								className={`${
									premiumTester ? 'translate-x-6' : 'translate-x-1'
								} inline-block h-4 w-4 transform rounded-full bg-gray-200 transition-all ease-in-out duration-200`}
							/>
						</Switch>
					</div>
				</div>
				<div className="p-3 text-center min-w-[100px]">
					<span className="text-xl font-bold block uppercase tracking-wide text-oldgray-600 dark:text-white transition-all duration-500">
						{topics.length}
					</span>
					<span className="text-sm text-gray-500 font-semibold dark:text-gray-400 transition-all duration-500">
						Topics
					</span>
				</div>
				<div className="p-3 text-center min-w-[100px]">
					<span className="text-xl font-bold block uppercase tracking-wide text-oldgray-600 dark:text-white transition-all duration-500">
						{dashboards.length}
					</span>
					<span className="text-sm text-secondary-text-light font-semibold dark:text-secondary-text-dark transition-all duration-500">
						Dashboards
					</span>
				</div>
				<div className="p-3 text-center min-w-[100px]">
					<span className="text-lg font-bold block uppercase tracking-wide text-oldgray-600 dark:text-white transition-all duration-500">
						Block status
					</span>
					{isDisabled ? (
						<span className="text-sm text-red-500 font-semibold">
							{'Disabled'}
						</span>
					) : isBlocked ? (
						<span className="text-sm text-orange-500 font-semibold">
							{'Blocked'}
						</span>
					) : (
						<span className="text-sm text-green-600 font-semibold">
							{'Active'}
						</span>
					)}
				</div>
			</div>
		);
	};

	const getDashboardDailyImage = (dashboard: any, isBannerImage = false) => {
		const randomImage =
			'https://images.unsplash.com/photo-1499336315816-097655dcfbda?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2710&q=80';

		if (topics == null) return randomImage;

		try {
			const topic = topics.find(
				(topic) => topic._id === dashboard.selectedTopics[0]
			);
			if (topic == null) return randomImage;
			if (isBannerImage) return topic.dailyImage.url;
			if (topic.dailyImage.url_low_res != null)
				return topic.dailyImage.url_low_res;
			return topic.dailyImage.url;
		} catch (e) {
			return randomImage;
		}
	};

	const getBannerImage = () => {
		const randomImage =
			'https://images.unsplash.com/photo-1499336315816-097655dcfbda?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2710&q=80';

		if (dashboards == null) return randomImage;
		// If no dashboard is found, getDashboardDailyImage() will throw an exception and will return a random image
		const dashboard = dashboards.find(
			(dashboard) => dashboard._id === defaultDash
		);
		return getDashboardDailyImage(dashboard, true);
	};

	const saveUserReq = async () => {
		const dto = {
			userId: userId,
			username: username,
			email: email,
			roles: selectedRoles,
			premiumTester: premiumTester
		};

		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data: response } = await axios.put(
			globalEnv.BASE_URL + '/admin/user/update',
			dto,
			config
		);
		return response;
	};

	const updateUserEmailSubReq = async () => {
		const dto = {
			isSubbscribedToEmailing: !subscribedToEmails
		};

		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data: response } = await axios.put(
			globalEnv.BASE_URL + `/admin/user/${userId}/update-email-sub`,
			dto,
			config
		);
		return response;
	};

	const deleteUserReq = async () => {
		const { data: response } = await axios.delete(
			globalEnv.BASE_URL + '/admin/user/delete',
			{
				data: {
					userId: userId
				},
				headers: {
					Authorization: 'Bearer ' + ACCESS_TOKEN
				}
			}
		);
		return response;
	};

	const blockUserReq = async () => {
		const dto = {
			userId: userId,
			blockExpirationDate: '2030-10-18T10:00:00'
		};
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data: response } = await axios.put(
			globalEnv.BASE_URL + '/admin/user/block',
			dto,
			config
		);
		return response;
	};

	const unblockUserReq = async () => {
		const dto = {
			userId: userId
		};
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data: response } = await axios.put(
			globalEnv.BASE_URL + '/admin/user/unblock',
			dto,
			config
		);
		return response;
	};

	const saveUser = useMutation(saveUserReq, {
		onMutate: () => setIsSaving(true),
		onSuccess: () => {
			setIsSaving(false);
			setIsChanged(false);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.SUCCESS,
					message: 'Successfully updated user info.'
				})
			);
		},
		onError: () => {
			setIsSaving(false);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't update user info."
				})
			);
		}
	});

	const deleteUser = useMutation(deleteUserReq, {
		onSuccess: () => {
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.SUCCESS,
					message: 'User deleted.'
				})
			);
			goBackToList();
		},

		onError: () => {
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't delete user."
				})
			);
		}
	});

	const blockUser = useMutation(blockUserReq, {
		onMutate: () => {
			setIsBlocked(true);
		},

		onSuccess: () => {
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.SUCCESS,
					message: 'User is blocked.'
				})
			);
		},

		onError: () => {
			setIsBlocked(false);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't block user."
				})
			);
		}
	});

	const unblockUser = useMutation(unblockUserReq, {
		onMutate: () => {
			setIsBlocked(false);
		},
		onSuccess: () => {
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.SUCCESS,
					message: 'User is not blocked anymore.'
				})
			);
		},
		onError: () => {
			setIsBlocked(true);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't unblock user."
				})
			);
		}
	});

	const updateUserEmailSub = useMutation(updateUserEmailSubReq, {
		onSuccess: () => {
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.SUCCESS,
					message: "User's subscription to emailing list updated."
				})
			);
		},
		onError: () => {
			setSubscribedToEmails(!subscribedToEmails);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't change user's subscription to emailing list."
				})
			);
		}
	});

	return (
		<div className="content-container">
			<Scrollbars autoHide>
				{isLoading ? (
					<LoadingAnimComponent />
				) : isError ? (
					<div className="h-full ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					<main className="">
						<div className="sticky top-0 z-30 w-full bg-gray-100/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-300 dark:border-gray-700 shadow-sm">
							<div className="px-4 py-3 flex flex-row">
								<div className="justify-start flex-1">
									{isBlocked ? (
										<button
											className="btn btn-outline btn-sm btn-active mr-1"
											type="button"
											style={{ transition: 'all .15s ease' }}
											onClick={() => unblockUser.mutate()}
										>
											Unblock
										</button>
									) : (
										<button
											className="btn btn-outline btn-sm btn-warning mr-1"
											type="button"
											style={{ transition: 'all .15s ease' }}
											onClick={() => blockUser.mutate()}
										>
											Block
										</button>
									)}
									<button
										className="btn btn-outline btn-sm btn-error mr-1"
										type="button"
										style={{ transition: 'all .15s ease' }}
										onClick={() => deleteUser.mutate()}
									>
										Delete
									</button>
								</div>
								<div className="justify-end flex flex-row items-center">
									{hasListContext && listNav.userIds!.length > 1 && (
										<>
											<button
												className="btn btn-outline btn-sm"
												type="button"
												title="Previous user"
												style={{ transition: 'all .15s ease' }}
												disabled={listNav.userIndex! <= 0}
												onClick={() => goToSibling(-1)}
											>
												<FaChevronLeft size={12} />
											</button>
											<span className="text-xs font-semibold mx-2 dark:text-gray-300 text-gray-600 whitespace-nowrap">
												{`${listNav.userIndex! + 1} / ${
													listNav.userIds!.length
												}`}
											</span>
											<button
												className="btn btn-outline btn-sm mr-3"
												type="button"
												title="Next user"
												style={{ transition: 'all .15s ease' }}
												disabled={
													listNav.userIndex! >= listNav.userIds!.length - 1
												}
												onClick={() => goToSibling(1)}
											>
												<FaChevronRight size={12} />
											</button>
										</>
									)}
									<button
										className="btn btn-outline btn-sm mr-1"
										type="button"
										style={{ transition: 'all .15s ease' }}
										onClick={() => goBackToList()}
									>
										Cancel
									</button>
									{isChanged && (
										<button
											disabled={isLoading ? true : false}
											type="button"
											className="btn btn-outline btn-sm btn-primary ml-1"
											onClick={() => saveUser.mutate()}
										>
											save
											{isSaving ? (
												<div className="pl-2">
													<svg
														role="status"
														className="w-5 h-5 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
														viewBox="0 0 100 101"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
															fill="currentColor"
														></path>
														<path
															d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
															fill="currentFill"
														></path>
													</svg>
												</div>
											) : (
												<h5 className="text-xs ml-2">💾</h5>
											)}
										</button>
									)}
								</div>
							</div>
						</div>
						<section className="relative block" style={{ height: '500px' }}>
							<div
								className="absolute top-0 w-full h-full bg-center bg-cover"
								style={{
									backgroundImage: `url('${getBannerImage()}')`
								}}
							>
								<span
									id="blackOverlay"
									className="w-full h-full absolute opacity-50 bg-black"
								></span>
							</div>
						</section>
						<section className="relative py-16 bg-gray-300 dark:bg-gray-600 transition-all duration-500">
							<div className="container mx-auto px-4">
								<div className="relative flex flex-col min-w-0 break-words bg-white dark:bg-gray-800 transition-all duration-500 w-full mb-6 shadow-xl rounded-lg -mt-64">
									<div className="px-6">
										<div className="flex flex-wrap justify-center">
											<div className="w-full lg:w-3/12 px-4 lg:order-2 flex justify-center">
												<div className=" mask mask-hexagon-2 flex rounded-full justify-center bg-gray-300 dark:bg-gray-700 border-none absolute -m-16">
													<LazyImage
														photo={
															photo != null
																? photo
																: `https://api.dicebear.com/7.x/bottts/svg?seed=${username}`
														}
														dimesnions={'140'}
														loaderDimesnions={80}
													/>
												</div>
											</div>
											<div className="flex flex-row lg:w-4/12 order-2 lg:order-3 self-center">
												<div className="w-full lg:text-left self-center">
													<SignupAndActivityDates />
												</div>
												{isProfile || (
													<div className="px-4 lg:text-right">
														<div className="py-6 px-3 lg:mt-0 sm:mt-0">
															<button
																className="btn btn-outline btn-sm btn-primary mb-1 mr-1"
																type="button"
																style={{ transition: 'all .15s ease' }}
															>
																Contact
															</button>
														</div>
													</div>
												)}
											</div>

											<div className="w-full mt-12 lg:mt-0 lg:w-4/12 px-4 order-1 ">
												<GeneralData />
											</div>
										</div>
										<div className="text-center lg:-mt-5">
											<Editable
												text={username}
												placeholder="Username"
												childRef={usernameRef}
												type="input"
												className="text-4xl font-semibold leading-normal"
												textColor="dark:text-primary-text-dark text-primary-text-light transition-colors duration-500"
											>
												<input
													ref={usernameRef}
													type="text"
													name="name"
													className="shadow rounded text-4xl font-semibold leading-normal text-primary-text-light text-center
													dark:bg-base-bg-dark transition-all duration-500 dark:text-primary-text-dark"
													placeholder=""
													value={username}
													onChange={(e) => {
														setUsername(e.target.value);
														setIsChanged(true);
													}}
												/>
											</Editable>

											<div className="text-sm leading-normal font-bold">
												<Editable
													text={email}
													placeholder="Email"
													childRef={emailRef}
													type="input"
													className="font-semibold leading-normal transition-colors duration-500"
													textColor="text-gray-600 dark:text-gray-100 transition-all duration-500"
												>
													<input
														ref={emailRef}
														type="text"
														name="name"
														className="shadow rounded font-semibold leading-normal text-gray-600 text-center dark:bg-gray-800
														dark:text-gray-100 transition-all duration-500"
														placeholder=""
														value={email}
														onChange={(e) => {
															setEmail(e.target.value);
															setIsChanged(true);
														}}
													/>
												</Editable>
												<div className="text-sm leading-normal mb-2 text-gray-500 dark:text-gray-500 font-bold uppercase max-w-lg mx-auto mt-2">
													<i className="fas fa-map-marker-alt mr-2 text-sm"></i>{' '}
													{'Roles'}
													<Multiselect
														selectedRoles={selectedRoles}
														setSelectedRoles={setSelectedRoles}
														setIsChanged={setIsChanged}
													/>
												</div>
											</div>

											<div className="flex flex-col lg:flex-row self-center justify-center mt-4">
												<div className="mx-auto lg:mx-2 w-full sm:w-96 mb-5 lg:mb-0">
													<div
														className="block p-6 max-w-sm mx-auto rounded-xl border shadow-xl
															bg-card-light border-border-light dark:bg-card-dark dark:border-border-dark
															transition-colors duration-500"
													>
														<h3 className="mb-6 text-2xl font-bold tracking-tight text-oldgray-800 dark:text-white">
															User General Data
														</h3>

														<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
															ID: {userId}
														</h5>
														<div className="flex flex-row mx-auto justify-center mb-2">
															<h5 className="text-gray-700 dark:text-gray-400 font-semibold mr-1">
																{'Location: '}
															</h5>
															{
																<Editable
																	text={location}
																	placeholder="Location"
																	childRef={locationRef}
																	type="input"
																	className="font-semibold leading-normal transition-colors duration-500"
																	textColor="text-gray-500 dark:text-gray-100 transition-all duration-500"
																>
																	<input
																		ref={locationRef}
																		type="text"
																		name="name"
																		className="shadow rounded font-semibold leading-normal
																			text-gray-500 text-center dark:bg-gray-800
																			dark:text-gray-100 transition-all duration-500"
																		placeholder=""
																		value={location}
																		onChange={(e) =>
																			setLocation(e.target.value)
																		}
																	/>
																</Editable>
															}
														</div>
														<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
															Gender: Male
														</h5>

														<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-0">
															Delete Date:
														</h5>
														<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-0">
															N/A
														</h5>
													</div>
												</div>

												<div className="w-full sm:w-96 mx-auto lg:mx-2">
													<div
														className="block p-6 max-w-sm mx-auto rounded-xl border shadow-xl
													bg-card-light border-border-light dark:bg-card-dark dark:border-border-dark
													transition-colors duration-500"
													>
														<h3 className="mb-6 text-2xl font-bold tracking-tight text-oldgray-800 dark:text-white">
															User authentication Data
														</h3>

														<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
															Is Social: {isSocial ? 'True' : 'False'}
														</h5>

														<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
															Login attempts: {loginAttempts}
														</h5>
														<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
															{'Block expires: '}
															{blockExpires != null
																? moment(blockExpires).format(
																		'MM/DD/YYYY - HH:mm'
																  )
																: 'N/A'}
														</h5>
														<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-0">
															Forgot password ID:
														</h5>
														<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-0">
															N/A
														</h5>
													</div>
												</div>

												<div className="w-full sm:w-96 mx-auto lg:mx-2">
													<div
														className="block p-6 max-w-sm mx-auto rounded-xl border shadow-xl
													bg-card-light border-border-light dark:bg-card-dark dark:border-border-dark
													transition-colors duration-500 relative"
													>
														{subscriptions.findIndex(
															({ subscriptionStatus }) =>
																subscriptionStatus === 'active'
														) > -1 && (
															<div
																style={{
																	position: 'absolute',
																	top: '18px',
																	right: '-36px',
																	backgroundColor:
																		'rgb(147 51 234 / var(--tw-bg-opacity))',
																	color: 'white',
																	padding: '4px 25px',
																	fontWeight: 'bold',
																	fontSize: '1rem',
																	transform: 'rotate(45deg)',
																	zIndex: 10,
																	boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
																	letterSpacing: '2px',
																	textTransform: 'uppercase',
																	pointerEvents: 'none',
																	whiteSpace: 'nowrap',
																	width: 'fit-content'
																}}
															>
																Premium
															</div>
														)}

														<h3 className="mb-6 text-2xl font-bold tracking-tight text-oldgray-800 dark:text-white">
															User Subsciptions Data
														</h3>
														{subscriptions.map((sub: any, index: number) => {
															let statusColor = 'text-gray-700 ';
															switch (sub.subscriptionStatus) {
																case SubscriptionStatus.ACTIVE:
																	statusColor = 'text-green-600 ';
																	break;
																case SubscriptionStatus.PAST_DUE:
																case SubscriptionStatus.UNPAID:
																	statusColor = 'text-orange-500 ';
																	break;
																case SubscriptionStatus.CANCELED:
																case SubscriptionStatus.INCOMPLETE_EXPIRED:
																case SubscriptionStatus.PAUSED:
																	statusColor = 'text-red-500 ';
																	break;
																case SubscriptionStatus.TRIALING:
																	statusColor = 'text-blue-500 ';
																	break;
																case SubscriptionStatus.INCOMPLETE:
																	statusColor = 'text-yellow-500 ';
																	break;
																default:
																	statusColor = 'text-gray-700 ';
															}
															return (
																<h5
																	className="text-gray-700 dark:text-gray-400 font-semibold mb-2"
																	key={index}
																>
																	<span
																		className={
																			statusColor + 'font-bold text-xl'
																		}
																	>
																		{sub.subscriptionStatus
																			.replace('_', ' ')
																			.replace(/\b\w/g, (l: string) =>
																				l.toUpperCase()
																			)}
																	</span>
																	{' Start : '}
																	{sub.subscriptionStartDate != null
																		? moment(sub.subscriptionStartDate).format(
																				'MM/DD/YYYY - HH:mm'
																		  )
																		: 'N/A'}
																	{' End : '}
																	{sub.subscriptionEndDate != null
																		? moment(sub.subscriptionEndDate).format(
																				'MM/DD/YYYY - HH:mm'
																		  )
																		: 'N/A'}
																</h5>
															);
														})}

														{subscriptions.findIndex(
															({ subscriptionStatus }) =>
																subscriptionStatus === 'active'
														) > -1 && (
															<button
																type="button"
																title="Copy cancellation link"
																onClick={copyCancellationLink}
																className="w-full text-left text-gray-700 dark:text-gray-400 font-semibold mb-2 p-4 bg-gray-200 dark:bg-gray-900 hover:bg-gray-300 dark:hover:bg-gray-800 rounded-lg transition-colors duration-300"
															>
																<span className="flex items-center gap-2">
																	Cancellation Link:
																	{isCancellationLinkCopied ? (
																		<BsCheckLg className="h-3 w-3" />
																	) : (
																		<BsClipboard className="h-3 w-3" />
																	)}
																</span>
																<span className="block break-all text-xs underline-offset-2 hover:underline">
																	{cancellationLink}
																</span>
															</button>
														)}
													</div>
												</div>
											</div>
										</div>

										{/* Active Extensions */}
										{activeExtensions.length > 0 && (
											<div className="border-t lg:mt-8 border-gray-300 dark:border-gray-700 transition-all duration-500 text-center">
												<div className="grid grid-cols-3 gap-4 place-items-center">
													<div className="invisible"></div>
													<h3 className="mt-8 text-3xl font-bold text-gray-600 dark:text-gray-500">
														Active Extensions
													</h3>
													<div className="invisible"></div>
												</div>

												<div className="mx-auto">
													<div className="w-11/12 lg:w-fit carousel p-4 border-border-light dark:bg-card-dark dark:border-border-dark rounded-box mx-auto transition-all duration-500">
														{activeExtensions.map((extension, index) => (
															<div
																key={extension._id}
																className="relative carousel-item cursor-pointer"
																onClick={() =>
																	navigate(`/extension/${extension._id}`)
																}
															>
																<div
																	className="group block p-6 max-w-sm min-w-[300px] rounded-xl border shadow-xl mx-3
																	 border-border-light dark:bg-card-dark dark:border-border-dark transition-colors duration-500
																	relative overflow-hidden"
																>
																	{index === 0 &&
																		extension.userActivity?.[0]?.lastActive && (
																			<div className="absolute top-0 right-0 z-20 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg shadow-md">
																				Current
																			</div>
																		)}
																	{extension.topic?.dailyImage?.url_low_res && (
																		<div className="absolute inset-0 opacity-10">
																			<img
																				src={
																					extension.topic.dailyImage.url_low_res
																				}
																				alt={extension.topic.name}
																				className="w-full h-full object-cover"
																			/>
																		</div>
																	)}
																	<div className="relative z-10">
																		<h3 className="mb-6 text-2xl font-bold tracking-tight text-oldgray-800 dark:text-white">
																			{extension.name}
																		</h3>
																		<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
																			Topic: {extension.topic?.name || 'N/A'}
																		</h5>

																		<h5 className="text-gray-700 dark:text-gray-400 font-semibold mb-2">
																			Last Active:{' '}
																			{moment(
																				extension.userActivity[0]?.lastActive
																			).format('MM/DD/YYYY - HH:mm')}
																		</h5>
																	</div>
																</div>
															</div>
														))}
													</div>
												</div>
											</div>
										)}

										{/* CARD */}

										<div className="border-t mt-8 border-gray-300 dark:border-gray-700 transition-all duration-500">
											<div className="lg:mt-8">
												{/* <div className="grid grid-cols-2 lg:px-[10%] px-[3%] mb-3"> */}
												<div className="grid grid-cols-3 gap-4 place-items-center">
													<div className=" invisible"></div>
													<h3 className="mt-3 sm:mt-2 text-xl sm:text-3xl font-bold text-gray-600 dark:text-gray-500">
														Dashboards
													</h3>
													{isAddDashModalOpen && (
														<DashboardAddModal
															userId={userId}
															isOpen={isAddDashModalOpen}
															setOpen={setDashModalOpen}
															topics={topics}
															dashboards={dashboards}
															setDashboards={setDashboards}
														/>
													)}
													<button
														className="btn btn-outline btn-sm btn-primary ml-auto mt-3 mr-2 "
														onClick={() => {
															setDashModalOpen(true);
														}}
													>
														Add ➕
													</button>
												</div>
												<div className="mt-3">
													<div className="w-full lg:w-10/12 lg:px-4 mx-auto">
														<div className="">
															<div className="flex flex-wrap  lg:flex-grow justify-center">
																{dashboards.map((item: any) => (
																	<DashCard
																		key={item._id}
																		dashboardId={item._id}
																		userId={userId}
																		image={getDashboardDailyImage(item)}
																		name={item.title}
																		defaultDash={defaultDash}
																		singleDash={singleDash}
																		setDefaultDash={setDefaultDash}
																		dashboardList={dashboards}
																		setDashboards={setDashboards}
																	/>
																))}
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Devices */}

										{devices.length > 0 && (
											<div className="text-center">
												<div className="border-t mt-8 border-gray-300 dark:border-gray-700 transition-all duration-500">
													<h3 className="mt-4 text-3xl font-bold text-gray-600 dark:text-gray-500 text-center">
														Devices
													</h3>
												</div>
												{/* <div className="flex flex-col lg:flex-row self-center justify-center mt-5"> */}

												<div className="mx-auto mt-5 mb-3">
													<div className="w-fit max-w-full  carousel p-4 bg-card-light border-border-light dark:bg-card-dark dark:border-border-dark  rounded-box mx-auto transition-all duration-500">
														{devices.map((device, index) => (
															<DeviceCard
																_id={device._id}
																deviceId={device.deviceId}
																expirationDate={device.expiresAt}
																refreshToken={
																	'********' +
																	device.refreshToken.slice(
																		device.refreshToken.length - 7
																	)
																}
																number={index + 1}
															/>
														))}
													</div>
												</div>
											</div>
										)}

										{/* Topics */}
										<div className="border-t lg:mt-8 border-gray-300 dark:border-gray-700 transition-all duration-500 text-center">
											<div className="grid grid-cols-3 gap-4 place-items-center">
												<div className=" invisible"></div>
												<h3 className="mt-2 text-3xl font-bold text-gray-600 dark:text-gray-500">
													Topics
												</h3>

												{isAddTopicModalOpen && (
													<TopicAddModal
														isOpen={isAddTopicModalOpen}
														setOpen={setTopicModalOpen}
														selectedTopics={topics}
														setTopics={setTopics}
														userId={userId}
													/>
												)}
												<button
													className="btn btn-outline btn-sm btn-primary ml-auto mt-3 mr-2 "
													onClick={() => {
														dispatch(
															setSearchPlaceholderAction('Search for Topics')
														);
														setTopicModalOpen(true);
													}}
												>
													Add ➕
												</button>
											</div>

											<div className="mx-auto mt-5">
												<div className="w-11/12 lg:w-fit carousel p-4 bg-card-light border-border-light dark:bg-card-dark dark:border-border-dark  rounded-box mx-auto transition-all duration-500">
													{topics.map((item: any) => (
														<TopicCard
															key={item._id}
															topicId={item._id}
															userId={userId}
															name={item.name}
															image={item.backgroundImage}
															singleTopic={false}
															topicsList={topics}
															setTopics={setTopics}
														/>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</section>
					</main>
				)}
			</Scrollbars>
		</div>
	);
}
